import { useGetAllInvoiceParties } from "@modules/invoice-parties/services/useGetInvoiceParties";
import { useGetAllPartnerChannels } from "@modules/partner-channel/services/useGetAllPartnerChannels";
import { useGetAllPartners } from "@modules/partners/services/useGetAllPartners";
import { IPartnerRow } from "@modules/partners/utils/types";
import { useGetAllPublishers } from "@modules/publishers/services/useGetAllPublishers";
import { PublisherRow } from "@modules/publishers/utils/types";
import { useEffect, useMemo, useState } from "react";
import { PUSH_MODULES } from "../utils/constants";
import { InvoicePartyData, PartnerChannelData } from "../utils/types";

const BASE_API_PARAMS = {
  pageNumber: "1",
  pageSize: "3",
  includeInactive: true,
  searchQuery: "",
  order: "",
};

export const useGetUnPushedData = () => {
  const lastPush = useMemo(() => {
    const dashboardData = localStorage.getItem("dashboard-overview");
    return dashboardData ? JSON.parse(dashboardData).lastPushAt : null;
  }, []);

  const [invoicePartyData, setInvoicePartyData] = useState<InvoicePartyData[]>(
    [],
  );
  const [partnerChannelData, setPartnerChannelData] = useState<
    PartnerChannelData[]
  >([]);
  const [publisherData, setPublisherData] = useState<PublisherRow[]>([]);
  const [partnerData, setPartnerData] = useState<IPartnerRow[]>([]);

  const { data: invoicePartiesList, isLoading: invoicePartiesLoading } =
    useGetAllInvoiceParties({
      data: {
        ...BASE_API_PARAMS,
        orderBy: "updateTstamp",
        updatedSince: lastPush,
      },
    });

  const { data: partnerChannelsList, isLoading: partnerChannelsLoading } =
    useGetAllPartnerChannels({
      data: {
        ...BASE_API_PARAMS,
        orderBy: "updateTstamp",
        updatedSince: lastPush,
      },
    });

  const { data: publisherPartiesList, isLoading: publishersLoading } =
    useGetAllPublishers({
      data: {
        ...BASE_API_PARAMS,
        orderBy: "updatedAt",
        updatedSince: lastPush,
      },
    });

  const { data: partnersList, isLoading: partnersLoading } = useGetAllPartners({
    data: {
      ...BASE_API_PARAMS,
      titleID: "",
      partnerChannel: "",
      platform: "",
      invoiceParty: "",
      orderBy: "updatedAt",
      updatedSince: lastPush,
    },
  });

  useEffect(() => {
    if (invoicePartiesList?.data) {
      const data = invoicePartiesList.data.map((item) => ({
        ...item,
        id: item.uid,
      }));
      setInvoicePartyData(data as InvoicePartyData[]);
    }
  }, [invoicePartiesList]);

  useEffect(() => {
    if (partnerChannelsList?.data) {
      const data = partnerChannelsList.data.map((item) => ({
        ...item,
        // Ensure channelId is a number to match PartnerChannelData type
        channelId: Number(item.channelId),
      }));
      setPartnerChannelData(data as unknown as PartnerChannelData[]);
    }
  }, [partnerChannelsList]);

  useEffect(() => {
    if (publisherPartiesList?.data) {
      setPublisherData(publisherPartiesList.data);
    }
  }, [publisherPartiesList]);

  useEffect(() => {
    if (partnersList?.data) {
      setPartnerData(partnersList.data);
    }
  }, [partnersList]);

  const dataMap = useMemo(
    () => ({
      [PUSH_MODULES.INVOICE_PARTIES]: invoicePartyData,
      [PUSH_MODULES.PARTNER_CHANNELS]: partnerChannelData,
      [PUSH_MODULES.PUBLISHERS]: publisherData,
      [PUSH_MODULES.PARTNERS]: partnerData,
    }),
    [invoicePartyData, partnerChannelData, publisherData, partnerData],
  );

  const isLoading =
    invoicePartiesLoading ||
    partnerChannelsLoading ||
    publishersLoading ||
    partnersLoading;

  return {
    dataMap,
    isLoading,
    lastPush,
  };
};
